/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  Button,
  formatJSDate,
  Inline,
  Notification,
  Stack,
  Table,
  type TableProps,
  useModal,
} from '@omnipresentgroup/design-system';
import {
  useApproveTimesheetMutation,
  useGetTimesheet,
  useRequestChangesTimesheetMutation,
  useSubmitTimesheetMutation,
  useUpdateTimesheetMutation,
} from '@omnipresentgroup/domains';
import {
  type TimesheetArbeitszeitDay,
  TimesheetArbeitszeitStatusEnum,
  type TimesheetArbeitszeitTimeSlot,
  TimesheetPageActions,
} from '@omnipresentgroup/types';
import {
  type ColumnDef,
  type ColumnOrderState,
  getCoreRowModel,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import get from 'just-safe-get';
import { DateTime } from 'luxon';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormProvider, useForm } from 'react-hook-form';

import {
  TimesheetRequestChangesModal,
  TimesheetSummaryCard,
} from './components';
import {
  ColumnHeaders,
  useTimesheetDetailsTableConfig,
} from './hooks/useTimesheetDetailsTableConfig';

export const TIMESHEET_FORM_ID = 'timesheet-details-form';
export const TIMESHEET_ACTIONS_ID = 'timesheet-actions';

const EMPTY_ARRAY: TimesheetArbeitszeitDay[] = [];
const LOADING_ARRAY = Array.from(
  { length: 7 },
  () => ({}) as TimesheetArbeitszeitDay,
);

const today = DateTime.now().toISODate();

type Props = {
  employeeId: string;
  weekDate?: string;
  columnVisibility: Partial<{
    [key in keyof typeof ColumnHeaders]: boolean;
  }>;
  columnOrderOverride?: (keyof typeof ColumnHeaders)[];
  optionsOverride?: Omit<
    TableOptions<TimesheetArbeitszeitDay>,
    'data' | 'columns'
  >;
  columnsOverride?: ColumnDef<TimesheetArbeitszeitDay, unknown>[];
  emptyStateProps?: TableProps['emptyStateProps'];
  readonly?: boolean;
  actions?: TimesheetPageActions[];
};

export const TimesheetDetails = memo(
  ({
    employeeId,
    weekDate,
    columnVisibility,
    columnOrderOverride,
    optionsOverride,
    columnsOverride,
    emptyStateProps,
    readonly,
    actions,
  }: Props) => {
    const timesheetActionsWrapper = document.querySelector(
      `#${TIMESHEET_ACTIONS_ID}`,
    );

    const [defaultValues, setDefaultValues] = useState<any>(null);

    const { data: timesheet, isLoading } = useGetTimesheet({
      employeeId,
      weekDate: weekDate || today,
      enabled: !!employeeId,
    });

    useEffect(() => {
      if (timesheet) {
        const values = {
          days: timesheet.days.map((day) => ({
            summary: day.summary,
            date: day.date,
            timeSlots:
              day.timeSlots.length === 0
                ? [{}]
                : day.timeSlots.map((slot) => ({
                    type: { value: slot.type, label: '' },
                    startTime: slot.startTime && new Date(slot.startTime),
                    endTime: slot.endTime && new Date(slot.endTime),
                  })),
            type: day.type,
          })),
        };
        setDefaultValues(values);
      }
    }, [timesheet]);

    const formMethods = useForm({
      shouldUnregister: false,
    });

    useEffect(() => {
      // Given the nature of useForm that is only invoked once, we need to reset the form with the default values
      if (defaultValues) {
        formMethods.reset(defaultValues);
      }
    }, [defaultValues, formMethods]);

    const {
      mutateAsync: updateTimesheet,
      isPending: isUpdatingTimesheet,
      error: updateTimesheetError,
    } = useUpdateTimesheetMutation(timesheet?.id || '');

    const { mutateAsync: submitTimesheet, isPending: isSubmittingTimesheet } =
      useSubmitTimesheetMutation(timesheet?.id || '');

    const { mutateAsync: approveTimesheet, isPending: isApprovingTimesheet } =
      useApproveTimesheetMutation(timesheet?.id || '');

    const {
      mutateAsync: requestChangesTimesheet,
      isPending: isRequestingChangesTimesheet,
    } = useRequestChangesTimesheetMutation(timesheet?.id || '');

    const { modalProps } = useModal({});
    const [modalOpen, setModalOpen] = useState(false);

    const [columnVisibilityState, setColumnVisibilityState] = useState<
      Props['columnVisibility']
    >({
      // Default all columns to false visibility
      ...Object.fromEntries(
        Object.keys(ColumnHeaders).map((current) => [current, false]),
      ),
      // Enable columns from consumer
      ...columnVisibility,
    });

    const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
      columnOrderOverride || [],
    );

    const canEdit = useMemo(
      () =>
        timesheet?.status?.type === TimesheetArbeitszeitStatusEnum.DRAFT ||
        timesheet?.status?.type ===
          TimesheetArbeitszeitStatusEnum.CHANGES_REQUESTED,
      [timesheet?.status?.type],
    );

    const isReadonly = useMemo(
      () => (readonly === undefined ? !canEdit : readonly),
      [canEdit, readonly],
    );

    const { columns } = useTimesheetDetailsTableConfig({
      timesheet,
      columnsOverride,
      showFields: !isReadonly,
    });

    const tableOptions: Omit<
      TableOptions<TimesheetArbeitszeitDay>,
      'data' | 'columns'
    > = {
      state: {
        columnVisibility: columnVisibilityState,
        columnOrder,
      },
      getCoreRowModel: getCoreRowModel(),
      onColumnVisibilityChange: setColumnVisibilityState,
      onColumnOrderChange: setColumnOrder,
      ...optionsOverride,
    };

    const tableData = useMemo(
      () => (isLoading ? LOADING_ARRAY : (timesheet?.days ?? EMPTY_ARRAY)),
      [isLoading, timesheet?.days],
    );

    const table = useReactTable({
      data: tableData,
      columns: columns,
      ...tableOptions,
    });

    // Reset form when timesheet changes
    useEffect(() => {
      timesheet?.days && formMethods.reset();
    }, [formMethods, timesheet?.days]);

    // Handle saving timesheet
    const handleSaveTimesheet = useCallback(
      async (timeSheetDays: TimesheetArbeitszeitDay[]) => {
        if (!timeSheetDays?.length) {
          return;
        }

        const sanitizedDays = timeSheetDays.map((day) => {
          const { timeSlots } = day;

          // To clear the values without triggering actual BE validation errors
          // we need to replace the object with empty values, to an empty array
          const isSingleEmptySlot =
            timeSlots.length === 1 &&
            !timeSlots[0].startTime &&
            !timeSlots[0].endTime &&
            !timeSlots[0].type;

          return {
            ...day,
            timeSlots: isSingleEmptySlot ? [] : timeSlots,
            type: undefined,
          };
        });

        await updateTimesheet({ days: sanitizedDays });
      },
      [updateTimesheet],
    );

    // Handle form submission -> save timesheet
    const onSubmit = (data: any) => {
      const result = data?.days?.map(
        (day: { timeSlots: any[] }, index: number) => {
          const date = timesheet?.days[index].date;
          const timeSlots: TimesheetArbeitszeitTimeSlot[] | undefined =
            day?.timeSlots?.reduce((timeSlotResult, timeSlot) => {
              return Object.values(timeSlot).every(
                (element) => element === undefined,
              )
                ? timeSlotResult
                : [
                    ...timeSlotResult,
                    {
                      startTime:
                        timeSlot.startTime &&
                        typeof timeSlot.startTime !== 'string'
                          ? formatJSDate(timeSlot.startTime, `${date}'T'HH:mm`)
                          : '',
                      endTime:
                        timeSlot.endTime && typeof timeSlot.endTime !== 'string'
                          ? formatJSDate(timeSlot.endTime, `${date}'T'HH:mm`)
                          : '',
                      type: timeSlot.type?.value,
                    },
                  ];
            }, [] as TimesheetArbeitszeitTimeSlot[]);

          return {
            date,
            timeSlots,
          };
        },
      );
      handleSaveTimesheet(result as TimesheetArbeitszeitDay[]);
    };

    // Handle errors from API response
    useEffect(() => {
      if (updateTimesheetError) {
        const errors = get(updateTimesheetError, 'detail.body');
        if (errors && timesheet?.days) {
          for (const [index, day] of timesheet.days.entries()) {
            const dayIndex = index;
            for (const [timeSlotIndex, _timeSlot] of day.timeSlots.length > 0
              ? day.timeSlots.entries()
              : [{}].entries()) {
              // in case we have a new time slot in the ui
              const startTimeErrors: string[] = get(
                errors,
                `days.${dayIndex}.timeSlots.${timeSlotIndex}.startTime._errors`,
              );
              startTimeErrors?.length > 0 &&
                formMethods.setError(
                  `days[${dayIndex}].timeSlots[${timeSlotIndex}].startTime`,
                  {
                    type: 'custom',
                    message: startTimeErrors.toString(),
                  },
                );
              const endTimeErrors: string[] = get(
                errors,
                `days.${dayIndex}.timeSlots.${timeSlotIndex}.endTime._errors`,
              );
              endTimeErrors?.length > 0 &&
                formMethods.setError(
                  `days[${dayIndex}].timeSlots[${timeSlotIndex}].endTime`,
                  {
                    type: 'custom',
                    message: endTimeErrors.toString(),
                  },
                );
              const typeErrors: string[] = get(
                errors,
                `days.${dayIndex}.timeSlots.${timeSlotIndex}.type._errors`,
              );
              typeErrors?.length > 0 &&
                formMethods.setError(
                  `days[${dayIndex}].timeSlots[${timeSlotIndex}].type`,
                  {
                    type: 'custom',
                    message: typeErrors.toString(),
                  },
                );
            }
          }
        }
      }
    }, [formMethods, timesheet?.days, updateTimesheetError]);

    const TimesheetActions = () => {
      return (
        <Inline>
          {actions?.map((action) => {
            switch (action) {
              case TimesheetPageActions.SAVE: {
                const canSave =
                  (timesheet?.status?.type ===
                    TimesheetArbeitszeitStatusEnum.DRAFT ||
                    timesheet?.status?.type ===
                      TimesheetArbeitszeitStatusEnum.CHANGES_REQUESTED) &&
                  formMethods.formState.isDirty;

                return (
                  <Button
                    key="save-timesheet-action"
                    form={TIMESHEET_FORM_ID}
                    type="submit"
                    variant="secondary"
                    disabled={isLoading || isSubmittingTimesheet || !canSave}
                    loading={isUpdatingTimesheet}
                  >
                    Save
                  </Button>
                );
              }
              case TimesheetPageActions.SUBMIT: {
                const canSubmit =
                  (timesheet?.status?.type ===
                    TimesheetArbeitszeitStatusEnum.DRAFT ||
                    timesheet?.status?.type ===
                      TimesheetArbeitszeitStatusEnum.CHANGES_REQUESTED) &&
                  !formMethods.formState.isDirty;

                return (
                  <Button
                    key="submit-timesheet-action"
                    disabled={isLoading || isUpdatingTimesheet || !canSubmit}
                    onClick={() => submitTimesheet()}
                    loading={isSubmittingTimesheet}
                  >
                    Submit for approval
                  </Button>
                );
              }
              case TimesheetPageActions.APPROVE: {
                const canApprove =
                  timesheet?.status?.type ===
                  TimesheetArbeitszeitStatusEnum.SUBMITTED;
                return (
                  <Button
                    key="approve-timesheet-action"
                    disabled={
                      isLoading || isRequestingChangesTimesheet || !canApprove
                    }
                    onClick={() => approveTimesheet()}
                    loading={isApprovingTimesheet}
                  >
                    Approve
                  </Button>
                );
              }
              case TimesheetPageActions.REQUEST_CHANGES: {
                const canRequestChanges =
                  timesheet?.status?.type ===
                    TimesheetArbeitszeitStatusEnum.SUBMITTED ||
                  timesheet?.status?.type ===
                    TimesheetArbeitszeitStatusEnum.APPROVED;
                return (
                  <Inline gap="0" key="request-changes-timesheet-action">
                    <Button
                      variant="secondary"
                      disabled={
                        isLoading || isApprovingTimesheet || !canRequestChanges
                      }
                      onClick={() => setModalOpen(true)}
                      loading={isRequestingChangesTimesheet}
                    >
                      Request changes
                    </Button>
                    <TimesheetRequestChangesModal
                      modalProps={{
                        ...modalProps,
                        open: modalOpen,
                        onOpenChange: setModalOpen,
                      }}
                      handleSubmit={(data) => {
                        setModalOpen(false);
                        requestChangesTimesheet({ reason: data.reason });
                      }}
                      handleCancel={() => setModalOpen(false)}
                    />
                  </Inline>
                );
              }
              default: {
                return null;
              }
            }
          })}
        </Inline>
      );
    };

    return (
      <Stack gap="24" w="stretch">
        {timesheet?.status?.type ===
          TimesheetArbeitszeitStatusEnum.CHANGES_REQUESTED && (
          <Notification
            id="changes-requested-notification"
            intent="warning"
            title="Changes have been requested for this timesheet"
            description={`Reason: ${timesheet?.status?.detail}`}
          />
        )}
        <TimesheetSummaryCard
          summary={timesheet?.summary}
          isLoading={isLoading}
        />
        <FormProvider {...formMethods}>
          <form
            id={TIMESHEET_FORM_ID}
            noValidate
            onSubmit={formMethods.handleSubmit(onSubmit)}
          >
            <Stack gap="24">
              <Table
                id="timesheet-details"
                table={table}
                loading={isLoading}
                showHeader={false}
                usePagination={false}
                emptyStateProps={
                  emptyStateProps || {
                    id: 'timesheet-details-empty-state',
                    title: 'No timesheets found',
                    description:
                      'No timesheets found for the selected employee.',
                  }
                }
              />
            </Stack>
          </form>
        </FormProvider>
        {actions &&
          timesheetActionsWrapper &&
          createPortal(<TimesheetActions />, timesheetActionsWrapper)}
      </Stack>
    );
  },
);
